import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { StudioProject } from '../../constants';

interface GlassModalProps {
    project: StudioProject;
    onClose: () => void;
}

const GlassModal: React.FC<GlassModalProps> = ({ project, onClose }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    // Handle escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Handle mobile back button - push history state so back closes modal
    useEffect(() => {
        window.history.pushState({ modal: true }, '');
        const handlePopState = () => {
            onClose();
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [onClose]);

    // Prevent body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    return (
        <motion.div
            className="glass-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center', // Center on desktop
                justifyContent: 'center',
                padding: '0', // No padding on mobile
                background: 'rgba(0, 0, 0, 0.9)', // Darker backdrop
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
            }}
        >
            <style>{`
                @media (min-width: 768px) {
                    .glass-modal-overlay { padding: 1.5rem !important; }
                    .glass-modal { 
                        border-radius: 16px !important; 
                        height: 92dvh !important; 
                        max-height: none !important;
                    }
                }
                .mobile-hidden { display: none; }
                @media (min-width: 768px) { .mobile-hidden { display: flex !important; } }
            `}</style>

            <motion.div
                ref={modalRef}
                className="glass-modal"
                initial={{ y: '100%' }} // Slide up on mobile
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                style={{
                    width: '100%',
                    maxWidth: '1366px', // Standard Laptop/Desktop Width
                    height: '100dvh', // Full screen mobile
                    background: '#121212',
                    borderRadius: '0', // Square on mobile
                    border: `1px solid rgba(${hexToRgb(project.accentColor)}, 0.2)`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                {/* Mobile Drag Handle / Header */}
                <div style={{
                    padding: '0.75rem 1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    background: '#0a0a0a'
                }}>
                    <div>
                        <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                            {project.title}
                        </h2>
                    </div>

                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#fff',
                            padding: '0.4rem',
                            cursor: 'pointer'
                        }}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Iframe Container */}
                <div style={{ flex: 1, position: 'relative', background: '#fff' }}>
                    <iframe
                        ref={iframeRef}
                        src={project.liveUrl}
                        title={project.title}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        allowFullScreen
                    />
                </div>

                {/* Desktop-only Footer Info */}
                <div className="mobile-hidden" style={{
                    padding: '0.75rem 1.5rem', // Compact footer
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    background: '#0a0a0a',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {project.techStack.map((tech, i) => (
                            <span key={i} style={{
                                fontSize: '0.75rem',
                                color: 'rgba(255,255,255,0.5)',
                                padding: '0.2rem 0.6rem',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '12px'
                            }}>{tech}</span>
                        ))}
                    </div>
                    <a href={project.liveUrl} target="_blank" rel="noreferrer" style={{
                        color: project.accentColor, textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem'
                    }}>
                        Open New Tab &rarr;
                    </a>
                </div>
            </motion.div>
        </motion.div>
    );
};

// Helper function to convert hex to RGB
function hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '255, 255, 255';
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
}

export default GlassModal;
